/* eslint-disable max-lines */
import type { AdditionalStatsType, PageType, UserDataType } from '@app-types/'
import Input from '@components/Input/Input'
import GenericModal from '@components/Modal/Modal'
import { contentLibrary } from '@components/shared/consts'
import { useUserData } from '@contexts/UserDataContext'
import dayjs from 'dayjs'
import { calculateAndGetNewStats, StatsDateOption } from 'helpers/stats'
import { useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { FaCalendarPlus } from 'react-icons/fa6'
import ToolbarButton from '../shared/PaginationAndSettingsRow/ToolbarButton'

const CUSTOM_STAT_OPTION = '__custom__'

// Validates that the input contains only English letters, numbers, spaces, and basic punctuation
const isValidCustomStatName = (name: string): boolean => {
  const regex = /^[a-zA-Z0-9\s\-_]+$/
  return regex.test(name) && name.trim().length > 0 && name.length <= 50
}

export const additionalStats: {
  name: string
  type: AdditionalStatsType | string
  color: string
}[] = [
  {
    name: 'Kaza Namazı',
    type: 'kazaNamazi',
    color: '#f59e0b',
  },
  {
    name: 'Nafile Namaz',
    type: 'nafileNamaz',
    color: '#c05621',
  },
  {
    name: 'Teheccüd Namazı',
    type: 'teheccudNamazi',
    color: '#9b2c2c',
  },
  {
    name: 'Oruç',
    type: 'oruc',
    color: '#e85d04',
  },
  {
    name: 'Teravih',
    type: 'teravih',
    color: '#10b981',
  },
  {
    name: 'Ayet Okuma',
    type: 'ayet_okuma',
    color: '#10b981',
  },
]

const getUsersPossibleBackwardsCorrectionDays = (stats: UserDataType['stats']) => {
  const filledDays = Object.values(stats).filter((stat) => stat.score > 0).length
  const percentage = Math.round((filledDays / 65) * 100)
  return percentage <= 50 ? 3 : Math.round((percentage - 50) / 10) + 3
}

const AddToStatsModal = ({
  fromReadingPage = false,
  defaultType = 'kuran',
  readPageCount,
  currentPage,
}: {
  fromReadingPage?: boolean
  defaultType?: PageType
  readPageCount?: number
  currentPage?: number
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [saveToBookmarks, setSaveToBookmarks] = useState('no')
  const [selectedType, setSelectedType] = useState<string>(defaultType)
  const [customStatName, setCustomStatName] = useState('')
  const { userData, updateUserData, updateMultipleUserData } = useUserData()
  const saveButtonRef = useRef<HTMLButtonElement>(null) as React.RefObject<HTMLElement>

  const correctionDays = getUsersPossibleBackwardsCorrectionDays(userData.stats)

  // Get user's previously added custom stats
  const userCustomStats = useMemo(
    () => userData.customStats || [],
    [userData.customStats],
  )

  useEffect(() => {
    const bookmarkCurrentPage = localStorage.getItem('bookmarkCurrentPage')
    if (bookmarkCurrentPage) {
      setSaveToBookmarks(bookmarkCurrentPage)
    }
  }, [])

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const rawPageType = formData.get('pageType') as string
    const amount = formData.get('amount') as string
    const date = formData.get('date') as StatsDateOption
    const bookmarkCurrentPage = formData.get('bookmarkCurrentPage') as string
    localStorage.setItem('bookmarkCurrentPage', bookmarkCurrentPage)

    // Determine the actual type to use
    let pageType: string
    if (rawPageType === CUSTOM_STAT_OPTION) {
      // Validate custom stat name
      if (!customStatName.trim()) {
        toast.error('Lütfen özel aktivite adını giriniz')
        return
      }
      if (!isValidCustomStatName(customStatName)) {
        toast.error(
          'Aktivite adı sadece İngilizce harfler, rakamlar, boşluk ve tire içerebilir',
        )
        return
      }
      pageType = customStatName.trim()
    } else {
      pageType = rawPageType
    }

    if (pageType && amount) {
      const newStats = calculateAndGetNewStats({
        type: pageType,
        readPageCount: Number(amount),
        userData,
        date,
      })

      // If it's a new custom stat, save it to user's custom stats list
      const isCustomStat = rawPageType === CUSTOM_STAT_OPTION
      const isNewCustomStat = isCustomStat && !userCustomStats.includes(pageType)
      const updatedCustomStats = isNewCustomStat
        ? [...userCustomStats, pageType]
        : userCustomStats

      if (bookmarkCurrentPage === 'yes' && fromReadingPage) {
        updateMultipleUserData({
          stats: newStats,
          [pageType]: currentPage,
          ...(isNewCustomStat && { customStats: updatedCustomStats }),
        })
      } else {
        if (isNewCustomStat) {
          updateMultipleUserData({
            stats: newStats,
            customStats: updatedCustomStats,
          })
        } else {
          updateUserData('stats', newStats)
        }
      }

      setIsOpen(false)
      setCustomStatName('')
      setSelectedType(defaultType)
    } else {
      toast.error('Lütfen sayı giriniz')
    }
  }

  return (
    <>
      <ToolbarButton
        title="Istatistiklere Ekleme Yap"
        ariaLabel="İstatistiklere Ekleme Yap"
        id="add-stats-button"
        onClick={() => {
          setIsOpen(true)
        }}
        className="text-main hover:bg-surface-subtle transition-all umi-event--add-stats-button-click"
      >
        <FaCalendarPlus />
      </ToolbarButton>
      <GenericModal
        id="add-to-stats"
        key="add-to-stats"
        isOpen={isOpen}
        variant="bottom-sheet"
        initialFocusRef={saveButtonRef}
        triggerCloseModal={() => setIsOpen(false)}
      >
        <div className="pt-2">
          <h1 className="mb-4 font-bold text-main text-xl">İstatistiklere Ekleme Yap</h1>
          <form onSubmit={handleFormSubmit}>
            <div className="flex flex-col">
              <label
                htmlFor="amount"
                className="mb-1 font-medium tracking-wide text-main"
              >
                Miktar
              </label>
              <Input
                placeholder="Kaç sayfa/rekat/gün?"
                type="number"
                name="amount"
                min={0}
                defaultValue={readPageCount! > 0 ? readPageCount : 1}
                id="amount"
                className="bg-surface-subtle text-main border border-main rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-secondary/50 appearance-none"
              />
            </div>
            <div className="flex flex-col mt-5 mb-2">
              <label
                htmlFor="pageType"
                className="mb-1 font-medium tracking-wide text-main"
              >
                Aktivite Türü
              </label>
              <select
                className="bg-surface-subtle text-main border border-main rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-secondary/50 appearance-none input-field"
                name="pageType"
                id="pageType"
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value)
                  if (e.target.value !== CUSTOM_STAT_OPTION) {
                    setCustomStatName('')
                  }
                }}
              >
                {contentLibrary.map((pageType) => (
                  <option key={pageType.type} value={pageType.type}>
                    {pageType.name}
                  </option>
                ))}
                {additionalStats.map((stat) => (
                  <option key={stat.type} value={stat.type}>
                    {stat.name}
                  </option>
                ))}
                {userCustomStats.length > 0 && (
                  <optgroup label="Özel Aktivitelerim">
                    {userCustomStats.map((customStat) => (
                      <option key={customStat} value={customStat}>
                        {customStat}
                      </option>
                    ))}
                  </optgroup>
                )}
                <option value={CUSTOM_STAT_OPTION}>Diğer (Özel Ekle)</option>
              </select>
            </div>
            {selectedType === CUSTOM_STAT_OPTION && (
              <div className="flex flex-col mt-3 mb-2">
                <label
                  htmlFor="customStatName"
                  className="mb-1 font-medium tracking-wide text-main"
                >
                  Özel Aktivite Adı
                </label>
                <Input
                  placeholder="Örn: Morning Walk, Yoga"
                  type="text"
                  name="customStatName"
                  id="customStatName"
                  value={customStatName}
                  onChange={(e) => setCustomStatName(e.target.value)}
                  maxLength={50}
                  className="bg-surface-subtle text-main border border-main rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-secondary/50 appearance-none"
                />
                <small className="mt-1 text-muted text-xs">
                  Sadece İngilizce harfler, rakamlar, boşluk ve tire kullanabilirsiniz.
                </small>
              </div>
            )}
            <div className="flex flex-col mt-5 mb-2">
              <label htmlFor="date" className="mb-1 font-medium tracking-wide text-main">
                Tarih <br />
                <small className="font-normal text-xs text-muted">
                  Bu kısımda göreceğiniz gün sayısı mevcut okuma durumunuza göre
                  değişkenlik gösterecektir.{' '}
                </small>
              </label>
              <select
                className="bg-surface-subtle text-main border border-main rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-secondary/50 appearance-none"
                name="date"
                id="date"
              >
                {Array.from(Array(correctionDays).keys()).map((day) => {
                  const date = dayjs().subtract(day, 'day').format('DD-MM-YYYY')
                  return (
                    <option key={date} value={date}>
                      {date} -{' '}
                      {day === 0 ? 'Bugün' : day === 1 ? 'Dün' : `${day} gün önce`}
                    </option>
                  )
                })}
              </select>
            </div>
            {fromReadingPage && (
              <div className="flex flex-col mt-5 mb-2">
                <label
                  htmlFor="bookmarkCurrentPage"
                  className="mb-1 text-sm font-medium tracking-wide text-main"
                >
                  Şu an bulunduğunuz sayfayı kaldığınız yer olarak kaydetmek ister
                  misiniz?
                </label>
                <select
                  className="bg-surface-subtle text-main border border-main rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-secondary/50 appearance-none"
                  name="bookmarkCurrentPage"
                  defaultValue={saveToBookmarks}
                >
                  <option key="yes" value="yes">
                    Evet
                  </option>
                  <option key="no" value="no">
                    Hayır
                  </option>
                </select>
              </div>
            )}
            <div className="mt-8 mb-2">
              <button
                type="submit"
                className="bg-main text-brand-secondary ring-1 ring-black/5 dark:ring-white/10 hover:bg-brand-secondary hover:text-white transition-colors shadow-sm px-5 py-3 rounded-xl focus:outline-none w-full font-semibold"
              >
                Kaydet
              </button>
            </div>
          </form>
        </div>
      </GenericModal>
    </>
  )
}

export default AddToStatsModal
